import React from "react";
import { useSelector } from "react-redux";
import {
  getSelectedSegment,
  getSelectedSegmentCharCount,
} from "../../redux/selectors/segmentBuilderSelectors";
import Wallpaper from "../SegmentBuilder/Shared/Wallpaper";
import { SegmentContainer } from "./SegmentSettings.styled";
import SegmentHeader from "./children/SegmentHeader";
import SegmentSize from "./children/SegmentSize";
import SegmentTitle from "./children/SegmentTitle";
import SegmentColor from "./children/SegmentColor";
import { Box, LinearProgress, Card } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TextView from "../TextView";
import IyugaTooltip from "../IyugaTooltip";
import { useSegmentPalettes } from "../../hooks/useSegmentPalettes";

export const charLimitInASegment = 20000;

const SegmentSettings = ({
  handleDeleteSegment,
  handleEmojiPicker,
  handleChange,
  isHideHeader = false,
}) => {
  const selectedArc = useSelector(getSelectedSegment);
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);
  const { fetchSegmentPalette } = useSegmentPalettes();
  const charPercentage = Math.min(
    (selectedSegmentCharCount / charLimitInASegment) * 100,
    100,
  );

  // Determine progress bar and background colors based on percentage
  let progressColor = "#8F38EF";
  let backgroundColor = "#F9FAFB";

  if (charPercentage >= 100) {
    progressColor = "#ED4337";
    backgroundColor = "#FCE5E0";
  } else if (charPercentage >= 80) {
    progressColor = "#E87033";
    backgroundColor = "#FEF5E7";
  }

  const {
    wallpaperTransparency,
    wallpaperResizing,
    wallpaperOverlayColor,
    bgWallpaper,
    isPattern,
    selectedColorType,
    patternColor,
    currentBg,
    segmentBgColor,
    title,
    color,
    patternId,
    resizeMode,
    wallpaperHuePercentage,
    wallpaperHueColor,
  } = selectedArc?.settings || {};

  // stg-0921-P0-edit-wheel-forgets-a-segments-blocks-after-a-segment-switch
  // (parity with dev `gordon-0920-editwheel-open-mutates-P0`): VIEW ACTIONS
  // NEVER WRITE. This used to run on EVERY Segment select (`selectedArc?.id`
  // change) for any Segment that happened to lack a `paletteId`, firing
  // POST generate-scheme + POST save-palette as a side effect of merely
  // OPENING the Segment. The resulting `updateSegmentColorPalette` dispatch
  // flips the wheel dirty, which arms the structural autosave
  // (pages/SegmentBuilder/index.jsx L2749-2787, STRUCTURAL_AUTOSAVE_DELAY_MS
  // = 1500) -- the single unattended PUT ~2.8s after save-palette that
  // persists whatever Redux happens to hold for the just-left Segment, and
  // so erases blocks the user had just added to it. Nothing defers that save
  // either: the palette-in-flight gate (index.jsx L2699/2771/2829) reads a
  // counter nothing on this branch ever increments (see useSegmentPalettes,
  // fixed in the same change). A palette is now derived only from a real
  // fill/colour replace (handleSegmentColorChange below).

  const handleSegmentColorChange = (color) => {
    handleChange("segmentColor", null, color);
    if (selectedArc) fetchSegmentPalette(color, selectedArc?.id);
  };

  // Compute segment view popup aspect ratio to match crop area dimensions.
  // SegmentView Container: width = max(354px, 25vw), height = min(900px, 85dvh)
  const segmentViewWidth = Math.max(354, window.innerWidth * 0.25);
  const segmentViewHeight = Math.min(900, window.innerHeight * 0.85);
  const segmentViewAspectRatio = segmentViewWidth / segmentViewHeight;

  return (
    <SegmentContainer>
      {!isHideHeader && (
        <SegmentHeader handleDeleteSegment={handleDeleteSegment} />
      )}

      {/* Text Character Limit Component */}
      <Card
        sx={{
          padding: 2,
          backgroundColor: backgroundColor,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #EAECF0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextView
              variant="subtitle"
              style={{ color: "#000000DE", marginRight: "10px" }}
            >
              Text Character Limit
            </TextView>

            <IyugaTooltip
              tooltip={`Each Segment can contain up to ${charLimitInASegment} text characters`}
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "#323232", cursor: "pointer" }}
              />
            </IyugaTooltip>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <TextView
              variant="largeText"
              style={{ color: "#000000DE", fontWeight: 700 }}
            >
              {selectedSegmentCharCount.toLocaleString()}
            </TextView>
            <TextView variant="largeText" style={{ color: "#667085" }}>
              out of {charLimitInASegment.toLocaleString()}
            </TextView>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={charPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#EAECF0",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              backgroundColor: progressColor,
            },
          }}
        />
      </Card>

      <SegmentTitle
        handleEmojiPicker={handleEmojiPicker}
        title={title || ""}
        onTitleChange={(value) =>
          handleChange("segmentTitle", null, value || "")
        }
      />
      <SegmentSize />

      {/* <SegmentIcon
        selectedIcon={icon}
        onIconSelect={(value) => handleChange('segmentIcon', null, value)}
      /> */}

      <Wallpaper
        // customCategories={['Patterns', 'Image']}
        showAccordion={true}
        cropAspectRatio={segmentViewAspectRatio}
        huePercentage={wallpaperHuePercentage ?? 0}
        huePercentageChange={(value) =>
          handleChange("wallpaperHuePercentage", null, value)
        }
        hueColor={wallpaperHueColor ?? "#ffffff"}
        hueColorChange={(value) =>
          handleChange("wallpaperHueColor", null, value)
        }
        slider1Value={wallpaperResizing}
        slider1Change={(value) =>
          handleChange("wallpaperResizing", null, value)
        }
        slider1BoxTitle={"Resizing"}
        slider2Value={wallpaperTransparency}
        slider2Change={(value) =>
          handleChange("wallpaperTransparency", null, value)
        }
        slider2BoxTitle={"Opacity"}
        handleWallpaperOverlayColorChange={(value) =>
          handleChange("addWallpaperOverlayColor", null, value)
        }
        wallpaperOverlayColor={wallpaperOverlayColor}
        handleSelectWallpaper={(value) =>
          handleChange("addBgWallpaper", 0, value)
        }
        handleSelectPattern={(value) => {
          handleChange("addPatternId", 1, value);
        }}
        isPattern={isPattern}
        bgWallpaper={bgWallpaper}
        background={wallpaperOverlayColor}
        handlePatternColorChange={(value) =>
          handleChange("addPatternColor", null, value)
        }
        handlePatternColorInitialChange={(value) =>
          handleChange("addPatternColorInitially", null, value)
        }
        handleSelectColorType={(val) =>
          handleChange("patternColorType", null, val)
        }
        selectedColorType={selectedColorType}
        patternColor={patternColor}
        onBgWallpaperColorChange={(value) =>
          handleChange("segmentBgColor", null, value)
        }
        currentBg={currentBg}
        segmentBgColor={segmentBgColor}
        patternId={patternId}
        isShowTransparencySlider={false}
        handleSelectBackgroundResizeMode={(value) =>
          handleChange("addResizeMode", null, value)
        }
        resizeMode={resizeMode}
        handleGradientColor={(value) =>
          handleChange("segmentBgGradientColor", null, value)
        }
      />
      <SegmentColor
        showTabs={false}
        onColorChange={(value) => handleSegmentColorChange(value)}
        currentColor={color}
        showSuggestedColorPalette={false}
      />

      {/* <ColorPalette
        title="Segment Palette"
        disabled
        generateAutomatically={true}
      /> */}
    </SegmentContainer>
  );
};

export default SegmentSettings;
